import {
  AlignmentType,
  BorderStyle,
  Document,
  Footer,
  Header,
  HeightRule,
  ImageRun,
  PageNumber,
  Paragraph,
  ShadingType,
  Table,
  TableCell,
  TableRow,
  TextRun,
  VerticalAlign,
  WidthType,
  convertInchesToTwip,
} from 'docx';

// Types
import { TemplateProps } from '../../type';

// Utils
import { loadFontFaces, POPPINS_FONT_FACES } from '../../utils/fonts';
import { circularCropImageBuffer, fetchImageAsBuffer } from '../../utils/image';
import { loadCertificationIcons, loadSocialIcons } from '../../utils/icons';
import { removeGoogleImageSizeParam } from '@/utils/url';

// Constants
import {
  A4,
  AVATAR_SIZE_PX,
  COLORS,
  FONT_FAMILY,
  FONT_SIZES,
  ICON_SIZE,
  LOGO_HEIGHT_PX,
  LOGO_WIDTH_PX,
  NO_BORDER,
  PAGE_PADDING,
  SIDEBAR_WIDTH,
  SPACINGS,
} from './constants';

// Components
import {
  PersonalInfoSection,
  loadContactIcons,
} from './components/Sidebar/PersonalInfo';
import { SocialLinksSection } from './components/Sidebar/SocialLinks';
import { EducationSection } from './components/Sidebar/Education';
import { TechnicalSkillsSection } from './components/Sidebar/TechnicalSkills';
import {
  CertificatesSection,
  CERT_ICON_GEN_SIZE,
  loadCertLinkIcon,
} from './components/Sidebar/Certificates';
import { BioSection } from './components/Content/Bio';
import { ExperiencesSection } from './components/Content/Experiences';

export const generateDefaultDocx = async (
  data: TemplateProps['data'],
): Promise<Document> => {
  const { info, technicalSkills, journeys, educations, certs } = data ?? {};
  const {
    name = '',
    title = '',
    bio = '',
    avatar = '',
    avatarResume = '',
    previewImageBase64 = '',
  } = info ?? {};

  const avatarUrl =
    avatarResume || removeGoogleImageSizeParam(avatar) || previewImageBase64;
  const certOrgs = (certs ?? []).map((c) => c?.org ?? '');
  const socialUrls = (info?.links ?? [])
    .map((l) => l?.url ?? '')
    .filter(Boolean);

  const [
    fonts,
    contactIcons,
    certIcons,
    socialIcons,
    linkIconBuffer,
    logoData,
    avatarData,
  ] = await Promise.all([
    loadFontFaces(POPPINS_FONT_FACES),
    loadContactIcons(),
    loadCertificationIcons(certOrgs, CERT_ICON_GEN_SIZE),
    loadSocialIcons(socialUrls, ICON_SIZE),
    loadCertLinkIcon(),
    fetchImageAsBuffer('/images/agility-logo.png'),
    avatarUrl
      ? circularCropImageBuffer(avatarUrl, AVATAR_SIZE_PX)
      : Promise.resolve(null),
  ]);

  const headerChildren: Paragraph[] = logoData
    ? [
        new Paragraph({
          alignment: AlignmentType.RIGHT,
          children: [
            new ImageRun({
              data: logoData,
              transformation: {
                width: LOGO_WIDTH_PX,
                height: LOGO_HEIGHT_PX,
              },
              type: 'png',
              altText: {
                title: 'Logo',
                description: 'Company logo',
                name: 'logo',
              },
            }),
          ],
        }),
      ]
    : [];

  // Content area width in DXA (A4 minus left + right page padding)
  const CONTENT_WIDTH = A4.width - 2 * PAGE_PADDING;
  const sidebarParagraphs = [
    ...PersonalInfoSection(name, title, avatarData, info ?? {}, contactIcons),
    ...(info?.links?.length ? SocialLinksSection(info.links, socialIcons) : []),
  ];

  // Gap between sidebar/content and the divider line
  const SIDEBAR_GAP = convertInchesToTwip(0.1);
  const CONTENT_GAP = convertInchesToTwip(0.15);

  // Sidebar column is slightly wider than token to shift divider rightward
  const SIDEBAR_COL = SIDEBAR_WIDTH + convertInchesToTwip(0.25);

  // Card is narrower than sidebar to create visible gap from divider
  const CARD_WIDTH = SIDEBAR_COL - 2 * SIDEBAR_GAP;

  const CARD_BORDER = {
    style: BorderStyle.SINGLE,
    size: 4,
    color: COLORS.border,
  } as const;

  const profileCard = new Table({
    width: { size: CARD_WIDTH, type: WidthType.DXA },
    columnWidths: [CARD_WIDTH],
    borders: NO_BORDER,
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: CARD_WIDTH, type: WidthType.DXA },
            borders: {
              top: CARD_BORDER,
              bottom: CARD_BORDER,
              left: CARD_BORDER,
              right: CARD_BORDER,
            },
            margins: {
              top: SPACINGS.cardMarginY,
              bottom: SPACINGS.cardMarginY,
              left: SPACINGS.cardMarginX,
              right: SPACINGS.cardMarginX,
            },
            children: sidebarParagraphs.length
              ? sidebarParagraphs
              : [new Paragraph({})],
          }),
        ],
      }),
    ],
  });

  const sidebarBlocks = [
    profileCard,
    ...EducationSection(educations, contactIcons.location),
    ...TechnicalSkillsSection(technicalSkills),
    ...CertificatesSection(certs, certIcons, linkIconBuffer, CARD_WIDTH),
  ];

  const mainParagraphs = [
    ...BioSection(bio),
    ...ExperiencesSection(journeys, contactIcons.location),
  ];

  const twoColumnTable = new Table({
    width: { size: CONTENT_WIDTH, type: WidthType.DXA },
    columnWidths: [SIDEBAR_COL, CONTENT_WIDTH - SIDEBAR_COL],
    borders: NO_BORDER,
    rows: [
      new TableRow({
        children: [
          new TableCell({
            width: { size: SIDEBAR_COL, type: WidthType.DXA },
            borders: NO_BORDER,
            margins: { right: SIDEBAR_GAP },
            children:
              sidebarBlocks.length > 0 ? sidebarBlocks : [new Paragraph({})],
          }),
          new TableCell({
            width: { size: 0, type: WidthType.AUTO },
            borders: NO_BORDER,
            margins: { left: CONTENT_GAP },
            children:
              mainParagraphs.length > 0 ? mainParagraphs : [new Paragraph({})],
          }),
        ],
      }),
    ],
  });

  return new Document({
    fonts,
    styles: {
      default: {
        document: {
          run: {
            font: FONT_FAMILY,
            size: FONT_SIZES.base,
            color: COLORS.foreground,
          },
        },
      },
    },
    sections: [
      {
        properties: {
          page: {
            size: { width: A4.width, height: A4.height },
            margin: {
              left: PAGE_PADDING,
              right: PAGE_PADDING,
            },
          },
        },
        headers: { default: new Header({ children: headerChildren }) },
        footers: {
          default: new Footer({
            children: [
              new Table({
                alignment: AlignmentType.RIGHT,
                width: { size: 440, type: WidthType.DXA },
                columnWidths: [440],
                borders: NO_BORDER,
                rows: [
                  new TableRow({
                    height: { value: 700, rule: HeightRule.EXACT },
                    children: [
                      new TableCell({
                        borders: NO_BORDER,
                        margins: { top: 0, bottom: 0, left: 0, right: 0 },
                        verticalAlign: VerticalAlign.CENTER,
                        shading: {
                          type: ShadingType.SOLID,
                          color: COLORS.primary,
                          fill: COLORS.primary,
                        },
                        children: [
                          new Paragraph({
                            alignment: AlignmentType.CENTER,
                            spacing: { before: 0, after: 0 },
                            children: [
                              new TextRun({
                                children: [PageNumber.CURRENT],
                                font: FONT_FAMILY,
                                size: FONT_SIZES['2xl'],
                                bold: true,
                                color: COLORS.white,
                              }),
                            ],
                          }),
                        ],
                      }),
                    ],
                  }),
                ],
              }),
            ],
          }),
        },
        children: [twoColumnTable],
      },
    ],
  });
};
